import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentHead } from '@/shared/hooks/useDocumentHead';
import { trackRumAction } from '@/shared/observability/rum';
import { ConciergeChooserOptions } from '../chooser/ConciergeChooserOptions';
import {
  ConciergeCalendlyHost,
  useConciergeCalendly,
} from '../chooser/ConciergeChooserProvider';

/**
 * Public, login-optional Care Concierge page (Feature 104).
 * Renders shared two-option chooser (details form + paid consultation) inline so
 * shareable URL matches in-app behavior.
 * CareConciergeForm (behind "Send us your details") already handles guest
 * submission path (reCAPTCHA guest-session → /contact-requests/guest).
 */
export const CareConciergePage = () => {
  const navigate = useNavigate();
  const { tier, openCalendly, closeCalendly } = useConciergeCalendly('public-page');

  useDocumentHead({
    title: 'Care Concierge | bQuest',
    description:
      'Get help finding the right care — talk it through with our team or send us your details. Free options, no login required.',
  });

  useEffect(() => {
    trackRumAction('care_concierge_page_view', { viewportWidth: window.innerWidth });
    trackRumAction('concierge_chooser_opened', { source: 'public-page' });
  }, []);

  return (
    <div className="bg-background">
      <div className="container max-w-4xl px-4 md:px-6 py-8 md:py-12">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#026857]">
            Care Concierge
          </p>
          <h1 className="mt-1 text-3xl font-recoleta font-semibold text-[#111827]">
            Get help finding the right care.
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Tell us what&apos;s going on, or talk it through with our team — whichever is easier
            for you.
          </p>
        </header>

        {/* onFormSuccess is a no-op so the form's richer success screen (request
            ID + expected timeline) renders in place — the page has no dialog to
            close. onFormClose returns the visitor to the landing. */}
        <ConciergeChooserOptions
          source="public-page"
          onOpenCalendly={openCalendly}
          onFormClose={() => navigate('/')}
        />
      </div>

      <ConciergeCalendlyHost tier={tier} onClose={closeCalendly} />
    </div>
  );
};
